'use client';

import { c as _c } from "react/compiler-runtime";
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useId } from 'react';
import type { FieldShellSize } from '@/components/ui/field-shell-size';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/ui/utils';
export type { FieldShellSize } from '@/components/ui/field-shell-size';
export interface FieldShellRenderArgs {
  controlProps: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': true | undefined;
    disabled: boolean | undefined;
    readOnly: boolean | undefined;
    required: boolean | undefined;
  };
}
export interface FieldShellProps {
  /** Label content. String for plain text; ReactNode to compose styled segments (e.g. "(optional)" sub-text). */
  label?: ReactNode;
  description?: string | undefined;
  error?: string | undefined;
  size?: FieldShellSize | undefined;
  fullWidth?: boolean | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  id?: string | undefined;
  ariaDescribedBy?: string | undefined;
  containerClassName?: string | undefined;
  labelClassName?: string | undefined;
  descriptionClassName?: string | undefined;
  errorClassName?: string | undefined;
  children: (args: FieldShellRenderArgs) => ReactNode;
}
function createFieldShellVariants(): ReturnType<typeof cva<{
  size: {
    sm: string;
    default: string;
    lg: string;
    xl: string;
  };
  fullWidth: {
    true: string;
    false: string;
  };
}>> {
  return cva('flex flex-col gap-1.5', {
    variants: {
      size: {
        sm: '[&_input]:h-8 [&_textarea]:min-h-8 [&_[data-slot=select-trigger]]:h-8 [&_input]:text-body',
        default: '[&_input]:h-9 [&_textarea]:min-h-9 [&_[data-slot=select-trigger]]:h-9',
        lg: '[&_input]:h-10 [&_textarea]:min-h-10 [&_[data-slot=select-trigger]]:h-10',
        xl: '[&_input]:h-12 [&_textarea]:min-h-12 [&_[data-slot=select-trigger]]:h-12'
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto'
      }
    },
    defaultVariants: {
      size: 'lg',
      fullWidth: true
    }
  });
}
const fieldShellVariants: ReturnType<typeof createFieldShellVariants> = createFieldShellVariants();
function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}
function fieldShellLabelTextClass(size: FieldShellSize | undefined): string {
  return size === 'sm' || size === 'default' ? 'text-caption' : 'text-body';
}
export function FieldShell(t0) {
  const $ = _c(54);
  const {
    label,
    description,
    error,
    size,
    fullWidth: t1,
    required,
    disabled,
    readOnly,
    id,
    ariaDescribedBy,
    containerClassName,
    labelClassName,
    descriptionClassName,
    errorClassName,
    children
  } = t0;
  const fullWidth = t1 === undefined ? true : t1;
  const generatedId = useId();
  let t2;
  if ($[0] !== generatedId || $[1] !== id) {
    t2 = isNonEmptyString(id) ? id : generatedId;
    $[0] = generatedId;
    $[1] = id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const controlId = t2;
  const hasLabel = label !== undefined && label !== null && label !== false && label !== "";
  let t3;
  if ($[3] !== description) {
    t3 = isNonEmptyString(description);
    $[3] = description;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const hasDescription = t3;
  let t4;
  if ($[5] !== error) {
    t4 = isNonEmptyString(error);
    $[5] = error;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const hasError = t4;
  const descriptionId = hasDescription ? `${controlId}-description` : undefined;
  const errorId = hasError ? `${controlId}-error` : undefined;
  let t5;
  if ($[7] !== ariaDescribedBy || $[8] !== descriptionId || $[9] !== errorId) {
    t5 = [ariaDescribedBy, descriptionId, errorId].filter(isNonEmptyString);
    $[7] = ariaDescribedBy;
    $[8] = descriptionId;
    $[9] = errorId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const describedByIds = t5;
  const mergedAriaDescribedBy = describedByIds.length > 0 ? describedByIds.join(" ") : undefined;
  const t6 = hasError ? true : undefined;
  let t7;
  let t8;
  let t9;
  if ($[11] !== children || $[12] !== containerClassName || $[13] !== controlId || $[14] !== disabled || $[15] !== fullWidth || $[16] !== hasLabel || $[17] !== label || $[18] !== labelClassName || $[19] !== mergedAriaDescribedBy || $[20] !== readOnly || $[21] !== required || $[22] !== size || $[23] !== t6) {
    const controlProps = {
      id: controlId,
      "aria-describedby": mergedAriaDescribedBy,
      "aria-invalid": t6,
      disabled,
      readOnly,
      required
    };
    if ($[27] !== containerClassName || $[28] !== fullWidth || $[29] !== size) {
      t7 = cn(fieldShellVariants({
        size,
        fullWidth
      }), containerClassName);
      $[27] = containerClassName;
      $[28] = fullWidth;
      $[29] = size;
      $[30] = t7;
    } else {
      t7 = $[30];
    }
    if ($[31] !== controlId || $[32] !== hasLabel || $[33] !== label || $[34] !== labelClassName || $[35] !== required || $[36] !== size) {
      t8 = hasLabel && <Label htmlFor={controlId} className={cn(fieldShellLabelTextClass(size), labelClassName)}>{label}{required === true && <span aria-hidden={true}> *</span>}</Label>;
      $[31] = controlId;
      $[32] = hasLabel;
      $[33] = label;
      $[34] = labelClassName;
      $[35] = required;
      $[36] = size;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    t9 = children({
      controlProps
    });
    $[11] = children;
    $[12] = containerClassName;
    $[13] = controlId;
    $[14] = disabled;
    $[15] = fullWidth;
    $[16] = hasLabel;
    $[17] = label;
    $[18] = labelClassName;
    $[19] = mergedAriaDescribedBy;
    $[20] = readOnly;
    $[21] = required;
    $[22] = size;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t10;
  if ($[38] !== description || $[39] !== descriptionClassName || $[40] !== descriptionId || $[41] !== hasDescription) {
    t10 = hasDescription && <p id={descriptionId} className={cn("text-caption text-foreground-secondary", descriptionClassName)}>{description}</p>;
    $[38] = description;
    $[39] = descriptionClassName;
    $[40] = descriptionId;
    $[41] = hasDescription;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  let t11;
  if ($[43] !== error || $[44] !== errorClassName || $[45] !== errorId || $[46] !== hasError) {
    t11 = hasError && <p id={errorId} role="alert" className={cn("text-caption text-feedback-error-content", errorClassName)}>{error}</p>;
    $[43] = error;
    $[44] = errorClassName;
    $[45] = errorId;
    $[46] = hasError;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t7 || $[51] !== t8 || $[52] !== t9) {
    t12 = <div className={t7}>{t8}{t9}{t10}{t11}</div>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t7;
    $[51] = t8;
    $[52] = t9;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  return t12;
}